import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Clock3, ExternalLink } from "lucide-react";
import { BtnLink, Eyebrow } from "@/components/kit";
import { ProductGrid, ProductStatus, PRODUCT_LOGOS } from "@/components/platform/Products";
import { API } from "@/lib/api";
import { SPACES } from "@/config/platform";
import { LEGAL, LEGAL_UPDATED } from "@/config/legal";
import { WaitlistForm } from "@/components/platform/WaitlistForm";

function PageIntro({ eyebrow, title, children }) {
  return (
    <div className="stagger">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h1 className="mt-4 max-w-3xl text-4xl font-semibold leading-tight text-bx sm:text-5xl">{title}</h1>
      {children && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-soft">{children}</p>}
    </div>
  );
}

export function ApplicationsPage() {
  return (
    <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 md:py-20" data-testid="applications-page">
      <PageIntro eyebrow="Applications" title="Le catalogue UGC & CO.">
        Chaque application t'accompagne sur une étape de ton activité de créatrice. De nouveaux outils rejoindront le catalogue au fil du temps.
      </PageIntro>
      <div className="mt-12"><ProductGrid testId="catalog-products" /></div>
    </div>
  );
}

export function ProductPage() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    if (slug === "ugc-go") return;
    fetch(`${API}/products/${slug}`).then((r) => (r.ok ? r.json() : null)).then(setP).catch(() => setP(null));
  }, [slug]);
  if (slug === "ugc-go") return <Navigate to="/ugc-go" replace />;
  if (p === undefined) return <div className="mx-auto h-96 max-w-4xl animate-pulse px-5 py-20" />;
  if (p === null)
    return (
      <div className="mx-auto max-w-3xl px-5 py-24 text-center" data-testid="product-not-found">
        <h1 className="text-3xl font-semibold text-bx">Cette application est introuvable.</h1>
        <BtnLink to="/applications" className="mt-8" data-testid="product-back-catalog">Retour au catalogue</BtnLink>
      </div>
    );
  const external = p.status === "available" && p.cta_path?.startsWith("https://");
  const logo = PRODUCT_LOGOS[p.slug];
  return (
    <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8 md:py-20" data-testid={`product-page-${p.slug}`}>
      <BtnLink to="/applications" variant="ghost" size="sm" className="-ml-3" data-testid="product-back"><ArrowLeft className="h-4 w-4" />Applications</BtnLink>
      {logo && <img src={logo.full} alt={`Logo ${p.name}`} className="mt-6 w-full max-w-md rounded-[22px] border border-line" data-testid="product-page-logo" />}
      <div className="mt-6"><ProductStatus p={p} testId="product-page-status" /></div>
      <h1 className="mt-5 text-5xl font-semibold text-bx">{p.name}</h1>
      <p className="mt-4 text-xl text-ink/80">{p.tagline}</p>
      {p.description && <p className="mt-6 max-w-2xl text-base leading-relaxed text-soft">{p.description}</p>}
      {p.highlights?.length > 0 && (
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {p.highlights.map((h, i) => <li key={`${i}-${h}`} className="flex items-center gap-3 rounded-2xl border border-line bg-white px-4 py-3"><Check className="h-4 w-4 text-gold" />{h}</li>)}
        </ul>
      )}
      <div className="mt-10">
        {external ? (
          <a href={p.cta_path} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[52px] items-center gap-2 rounded-full bg-bx px-7 text-white hover:bg-bx-dark" data-testid="product-open-external">
            Ouvrir {p.name}<ExternalLink className="h-4 w-4" />
          </a>
        ) : p.status === "soon" ? (
          <p className="inline-flex items-center gap-2 rounded-2xl border border-gold/50 bg-white px-5 py-4 text-bx" data-testid="product-soon-note">
            <Clock3 className="h-4 w-4 text-gold" />{p.name} n'est pas encore disponible. Sa présentation sera mise à jour à son ouverture.
          </p>
        ) : null}
      </div>
      {p.status === "soon" && p.slug === "collaby" && <div className="mt-8 max-w-2xl"><WaitlistForm defaults={["collaby"]} testId="product-waitlist" /></div>}
    </div>
  );
}

export function SpacePage({ space }) {
  const s = SPACES[space];
  return (
    <div className="mx-auto max-w-5xl px-5 py-14 sm:px-8 md:py-20" data-testid={`space-page-${space}`}>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/60 bg-white px-3 py-1 text-xs font-semibold text-bx-2" data-testid="space-soon-badge">
        <Clock3 className="h-3.5 w-3.5 text-gold" />Bientôt disponible
      </span>
      <div className="mt-6"><PageIntro eyebrow={s.eyebrow} title={s.title}>{s.intro}</PageIntro></div>
      <h2 className="mt-14 font-display text-2xl font-semibold text-ink">Ce qui est prévu</h2>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {s.planned.map((it) => (
          <div key={it.t} className="rounded-[20px] border border-line bg-white p-6">
            <p className="font-display text-lg font-semibold text-bx">{it.t}</p>
            <p className="mt-2 text-sm leading-relaxed text-soft">{it.d}</p>
          </div>
        ))}
      </div>
      <p className="mt-8 rounded-2xl border border-gold/30 bg-gold-soft/50 px-5 py-4 text-sm text-[#5E4A2C]" data-testid="space-note">{s.note}</p>
      {space !== "le-cercle" && <div className="mt-8"><WaitlistForm defaults={[space]} testId="space-waitlist" /></div>}
      <div className="mt-12 flex flex-col gap-3 sm:flex-row">
        <BtnLink to="/ugc-go" size="lg" data-testid="space-cta-ugcgo">Commencer avec UGC GO</BtnLink>
        <BtnLink to="/applications" variant="outline" size="lg" data-testid="space-cta-apps">Voir les applications</BtnLink>
      </div>
    </div>
  );
}

export function LegalPage({ page }) {
  const l = LEGAL[page];
  return (
    <div className="mx-auto max-w-3xl px-5 py-14 sm:px-8 md:py-20" data-testid={`legal-page-${page}`}>
      <h1 className="text-4xl font-semibold text-bx">{l.title}</h1>
      <p className="mt-3 text-sm text-soft">Dernière mise à jour : {LEGAL_UPDATED}</p>
      <div className="mt-10 space-y-8">
        {l.sections.map((s) => (
          <section key={s.t}>
            <h2 className="font-display text-xl font-semibold text-ink">{s.t}</h2>
            <div className="mt-2 space-y-2 leading-relaxed text-soft">{s.p.map((t) => <p key={t}>{t}</p>)}</div>
          </section>
        ))}
      </div>
    </div>
  );
}
